import { useEffect, useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import {
  fetchSiteById,
  selectSelectedSite,
  selectSelectedSiteStatus,
} from "@/features/maps/mapsSlice"
import { selectCurrentUser } from "@/features/auth/authSlice"
import { updateCampingSiteStatus } from "@/services/api"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import styles from "@/styles/SiteDetailPage.module.css"
import { FaArrowLeft } from "react-icons/fa"
import { Star, Copy, MapPin, ExternalLink, Bookmark, Share2, Navigation } from "lucide-react"
import type { Tag as TagType } from "@/types/domain"
import { Button, Tag, ActionSheet } from "@/components/ui"
import { ReviewSection } from "@/components/site/ReviewSection"

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react"
import { Pagination } from "swiper/modules"
import "swiper/css"
import "swiper/css/pagination"

export const SiteDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const site = useAppSelector(selectSelectedSite)
  const status = useAppSelector(selectSelectedSiteStatus)
  const currentUser = useAppSelector(selectCurrentUser)

  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [isNavSheetOpen, setIsNavSheetOpen] = useState(false)

  const isAdmin = currentUser?.role === "ADMIN"

  useEffect(() => {
    if (id) {
      void dispatch(fetchSiteById(id))
      
      // Load bookmark state
      const bookmarks = JSON.parse(localStorage.getItem("bookmarks") || "[]")
      setIsBookmarked(bookmarks.includes(id))
    }
  }, [dispatch, id])

  const handleStatusChange = async (newStatus: "PENDING" | "APPROVED" | "REJECTED") => {
    if (!id) return
    try {
      await updateCampingSiteStatus(id, newStatus)
      if (newStatus === "APPROVED") alert("캠핑장이 승인되었습니다.")
      else if (newStatus === "REJECTED") alert("캠핑장이 거절되었습니다.")
      else alert("대기 상태로 변경되었습니다.")
      
      // 상태 변경 후 새로고침
      void dispatch(fetchSiteById(id))
    } catch (error) {
      console.error(`Failed to update site status to ${newStatus}:`, error)
      alert("상태 변경 처리 중 오류가 발생했습니다.")
    }
  }

  const handleEdit = () => {
    if (!id) return
    navigate(`/site/register/${id}`)
  }

  const handleToggleBookmark = () => {
    if (!id) return
    const bookmarks = JSON.parse(localStorage.getItem("bookmarks") || "[]")
    let newBookmarks
    if (isBookmarked) {
      newBookmarks = bookmarks.filter((bid: string) => bid !== id)
    } else {
      newBookmarks = [...bookmarks, id]
    }
    localStorage.setItem("bookmarks", JSON.stringify(newBookmarks))
    setIsBookmarked(!isBookmarked)
  }

  const handleShare = async () => {
    if (!site) return
    const shareData = {
      title: site.name,
      text: site.description || `${site.name} - 차박차박에서 확인해보세요.`,
      url: window.location.href,
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData)
      } catch (error) {
        // User cancelled share or other error
        if ((error as Error).name !== 'AbortError') {
          console.error("Error sharing:", error)
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href)
        alert("링크가 복사되었습니다.")
      } catch (error) {
        console.error("Failed to copy:", error)
      }
    }
  }

  const groupedTags = useMemo(() => {
    if (!site?.tags) return {}
    return site.tags.reduce<Record<string, TagType[]>>((acc, tag) => {
      const groupName = tag.tagGroup.name
      if (!acc[groupName]) {
        acc[groupName] = []
      }
      acc[groupName].push(tag)
      return acc
    }, {})
  }, [site?.tags])

  const handleBackClick = () => {
    navigate(-1)
  }

  const openImageModal = (url: string) => {
    setSelectedImageUrl(url)
    document.body.style.overflow = "hidden"
  }

  const closeImageModal = () => {
    setSelectedImageUrl(null)
    document.body.style.overflow = ""
  }

  const handleCopyAddress = () => {
    if (!site?.address) return
    void navigator.clipboard.writeText(site.address)
    alert("주소가 복사되었습니다.")
  }

  const handleGetDirections = () => {
    setIsNavSheetOpen(true)
  }

  const navItems = [
    {
      label: "카카오맵으로 길찾기",
      onClick: () => {
        if (!site) return
        const url = `https://map.kakao.com/link/to/${site.name},${site.latitude},${site.longitude}`
        window.open(url, "_blank")
      },
      icon: <Navigation size={18} />,
    },
    {
      label: "네이버 지도로 길찾기",
      onClick: () => {
        if (!site) return
        const url = `https://map.naver.com/v5/directions/-/${site.longitude},${site.latitude},${site.name}/-/-`
        window.open(url, "_blank")
      },
      icon: <Navigation size={18} />,
    },
  ]

  const getTypeIcon = (nameOrIcon: string) => {
    const n = nameOrIcon.toLowerCase()
    // API에서 내려주는 icon 키값 및 이름 대응
    if (n === "parking" || n.includes("주차장")) return "/icons/markers/parking.png"
    if (n === "field" || n.includes("노지")) return "/icons/markers/field.png"
    if (n === "camping" || n.includes("캠핑") || n.includes("오토")) return "/icons/markers/camping.png"
    if (n === "facility" || n === "gear" || n.includes("설비") || n.includes("기어") || n.includes("편의") || n.includes("시설")) return "/icons/markers/gear.png"
    return "/icons/markers/default.png"
  }

  const siteTypeTags = useMemo(() => {
    // 필터링할 ID와 이름을 확장 (site_type, 카테고리 추가)
    const typeIdsOrNames = [
      "캠핑장 유형", "사이트 유형", "운영 형태", "카테고리",
      "site-type", "site_type", "sitetype", "siteType"
    ]
    return site?.tags.filter(tag => 
      typeIdsOrNames.includes(tag.tagGroup.name) || typeIdsOrNames.includes(tag.tagGroup.id)
    ) || []
  }, [site?.tags])

  if (status === "loading" || !site) {
    return (
      <div className={styles.container}>
        <Skeleton height={300} />
        <div className={styles.content}>
          <Skeleton height={36} width="80%" style={{ marginBottom: "1rem" }} />
          <Skeleton count={4} />
        </div>
      </div>
    )
  }

  if (status === "failed") {
    return <div>Error loading site details.</div>
  }

  return (
    <div className={styles.container}>
      <button onClick={handleBackClick} className={styles.backButton}>
        <FaArrowLeft />
      </button>
      
      <div className={styles.imageSliderWrapper}>
        {site.imageUrls && site.imageUrls.length > 0 ? (
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            className={styles.imageSwiper}
          >
            {site.imageUrls.map((url, index) => (
              <SwiperSlide key={index}>
                <img
                  src={url}
                  alt={`${site.name} ${index + 1}`}
                  className={styles.mainImage}
                  onClick={() => openImageModal(url)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className={styles.placeholderImage}>
            <Skeleton height="100%" width="100%" />
          </div>
        )}
      </div>

      {selectedImageUrl && (
        <div className={styles.imageModal} onClick={closeImageModal}>
          <img src={selectedImageUrl} alt="Full view" className={styles.modalImage} />
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.headerSection}>
          {siteTypeTags.length > 0 && (
            <div style={{ marginBottom: 'var(--space-2)', display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
              {siteTypeTags.map(tag => (
                <Tag key={tag.id} variant="primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px' }}>
                  <img 
                    src={getTypeIcon(tag.icon || tag.name)} 
                    alt="" 
                    style={{ width: '16px', height: '16px', objectFit: 'contain' }} 
                  />
                  {tag.name}
                </Tag>
              ))}
            </div>
          )}
          <div className={styles.titleRow}>
            <h1>{site.name}</h1>
            <div className={styles.actionButtons}>
              {isAdmin && (
                <Button onClick={handleEdit} variant="primary" size="sm" className={styles.editButton}>
                  수정
                </Button>
              )}
              <button 
                className={styles.iconButton} 
                onClick={handleToggleBookmark}
                title={isBookmarked ? "북마크 취소" : "북마크 추가"}
              >
                <Bookmark 
                  size={20} 
                  className={isBookmarked ? styles.bookmarked : ""} 
                  fill={isBookmarked ? "var(--color-primary-500)" : "none"}
                />
              </button>
              <button 
                className={styles.iconButton} 
                onClick={handleShare}
                title="공유하기"
              >
                <Share2 size={20} />
              </button>
            </div>
          </div>
          <div className={styles.ratingRow}>
            <Star size={16} className={styles.starIcon} />
            <span className={styles.ratingValue}>4.5</span>
            <span className={styles.reviewCount}>(128)</span>
          </div>
          <div className={styles.addressRow}>
            <div className={styles.addressText}>{site.address}</div>
            <div className={styles.addressActions}>
              <button 
                className={styles.iconButton} 
                onClick={handleCopyAddress}
                title="주소 복사"
              >
                <Copy size={18} />
              </button>
              <button 
                className={`${styles.iconButton} ${styles.primary}`}
                onClick={handleGetDirections}
                title="길찾기"
              >
                <MapPin size={18} />
              </button>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className={styles.adminActions}>
            {site.exposureStatus === "PENDING" ? (
              <>
                <Button
                  onClick={() => {
                    void handleStatusChange("APPROVED")
                  }}
                  variant="primary"
                  style={{ backgroundColor: "var(--color-success-500)", borderColor: "var(--color-success-500)", flex: 1 }}
                  size="sm"
                >
                  수락
                </Button>
                <Button
                  onClick={() => {
                    void handleStatusChange("REJECTED")
                  }}
                  variant="primary"
                  style={{ backgroundColor: "var(--color-error-500)", borderColor: "var(--color-error-500)", flex: 1 }}
                  size="sm"
                >
                  거절
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  void handleStatusChange("PENDING")
                }}
                variant="outline"
                style={{ flex: 1 }}
                size="sm"
              >
                대기 상태로 변경
              </Button>
            )}
          </div>
        )}

        {(site.operationType || site.operationDays || site.theme || site.animal) && (
          <div className={styles.section}>
            {site.operationType && (
              <div className={styles.infoRow}>
                <div className={styles.label}>운영 형태</div>
                <div className={styles.value}>{site.operationType}</div>
              </div>
            )}
            {site.operationDays && (
              <div className={styles.infoRow}>
                <div className={styles.label}>운영 기간</div>
                <div className={styles.value}>{site.operationDays}</div>
              </div>
            )}
            {site.theme && (
              <div className={styles.infoRow}>
                <div className={styles.label}>테마</div>
                <div className={styles.value}>{site.theme}</div>
              </div>
            )}
            {site.animal && (
              <div className={styles.infoRow}>
                <div className={styles.label}>반려동물</div>
                <div className={styles.value}>{site.animal}</div>
              </div>
            )}
          </div>
        )}

        {(site.facilities || site.features || site.intro) && (
          <div className={styles.section}>
            {site.facilities && (
              <div className={styles.infoRow}>
                <div className={styles.label}>주요 시설</div>
                <div className={styles.value}>{site.facilities}</div>
              </div>
            )}
            {site.features && (
              <div className={styles.infoRow}>
                <div className={styles.label}>특징</div>
                <div className={styles.value}>{site.features}</div>
              </div>
            )}
            {site.intro && (
              <div className={styles.infoRow}>
                <div className={styles.label}>추가 안내</div>
                <div className={styles.value}>{site.intro}</div>
              </div>
            )}
          </div>
        )}

        {(groupedTags["장소정보"] || groupedTags["즐길거리"] || groupedTags["요금"] || (site.referralLinks && site.referralLinks.length > 0)) && (
          <div className={styles.section}>
            {groupedTags["장소정보"] && (
              <div className={styles.infoRow}>
                <div className={styles.label}>장소정보</div>
                <div className={styles.tags}>
                  {groupedTags["장소정보"].map(tag => (
                    <Tag key={tag.id} variant="default">
                      {tag.icon && /\p{Emoji}/u.test(tag.icon) && (
                        <span style={{ marginRight: "4px" }}>{tag.icon}</span>
                      )}
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
            {groupedTags["즐길거리"] && (
              <div className={styles.infoRow}>
                <div className={styles.label}>즐길거리</div>
                <div className={styles.tags}>
                  {groupedTags["즐길거리"].map(tag => (
                    <Tag key={tag.id} variant="default">
                      {tag.icon && /\p{Emoji}/u.test(tag.icon) && (
                        <span style={{ marginRight: "4px" }}>{tag.icon}</span>
                      )}
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
            {groupedTags["요금"] && (
              <div className={styles.infoRow}>
                <div className={styles.label}>요금</div>
                <div className={styles.tags}>
                  {groupedTags["요금"].map(tag => (
                    <Tag key={tag.id} variant="default">
                      {tag.icon && /\p{Emoji}/u.test(tag.icon) && (
                        <span style={{ marginRight: "4px" }}>{tag.icon}</span>
                      )}
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
            {site.referralLinks && site.referralLinks.length > 0 && (
              <div className={styles.infoRow}>
                <div className={styles.label}>관련정보</div>
                <div className={styles.referralLinks}>
                  {site.referralLinks.map(link => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.referralLink}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "var(--color-primary-600)",
                        fontSize: "var(--font-size-sm)",
                        textDecoration: "none",
                        fontWeight: "var(--font-weight-medium)"
                      }}
                    >
                      <ExternalLink size={14} />
                      {link.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {Object.entries(groupedTags).filter(([name]) => 
          !["장소정보", "즐길거리", "요금", "캠핑장 유형", "사이트 유형", "운영 형태", "카테고리", "site-type", "site_type", "sitetype", "siteType"].includes(name)
        ).length > 0 && (
          <div className={styles.tagSection}>
            {Object.entries(groupedTags)
              .filter(([name]) => 
                !["장소정보", "즐길거리", "요금", "캠핑장 유형", "사이트 유형", "운영 형태", "카테고리", "site-type", "site_type", "sitetype", "siteType"].includes(name)
              )
              .map(([groupName, tags]) => (
                <div key={groupName} className={styles.tagGroup}>
                  <div className={styles.tags}>
                    {tags.map(tag => (
                      <Tag key={tag.id} variant="default">
                        {tag.icon && /\p{Emoji}/u.test(tag.icon) && (
                          <span style={{ marginRight: "4px" }}>{tag.icon}</span>
                        )}
                        {tag.name}
                      </Tag>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}

        {site.description && (
          <div className={styles.section}>
            <div className={styles.description}>{site.description}</div>
            {site.lineIntro && (
              <div className={styles.infoRow} style={{ marginTop: "var(--space-2)" }}>
                <div className={styles.value} style={{ fontStyle: "italic", color: "var(--color-text-secondary)", borderBottom: 'none' }}>
                  "{site.lineIntro}"
                </div>
              </div>
            )}
          </div>
        )}

        <ReviewSection siteId={site.id} />
      </div>

      <ActionSheet
        isOpen={isNavSheetOpen}
        onClose={() => setIsNavSheetOpen(false)}
        title="길찾기"
        description="원하시는 지도 앱을 선택하세요."
        items={navItems}
      />
    </div>
  )
}